import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import { TrendingUp } from "lucide-react";

import { getLookupsRequest } from "../../../store/Lookups/actions";
import {
  getPromotionRequestsRequest,
  editPromotionRequestsRequest,
} from "../../../store/PromotionRequests/actions";
import messages from "../../../assets/locale/messages";
import {
  adminPermissionModules,
  lookupsKeys,
  promotionRequestsStatus,
} from "../../../utils/Constants.js";
import {
  renderTextEllipsis,
  textContainOnlySpaces,
} from "../../../utils/Helpers";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import Breadcrumbs from "../../../components/BreadCrumbs";
import Filters from "../../../components/Filter";
import Avatar from "../../../components/Avatar";
import {
  UIActionModal,
  UIEmptyState,
  UIPaginationBar,
  UIRating,
  UIStatusTag,
  UITable,
  UIRowActionsMenu,
} from "../../../components/ui";
import "./PromotionsRequests.scss";
import { isAdmin, isSuperAdmin, isSupervisorManger } from "../../../utils/Permissions";
import { useAdminModulePermission } from "../../../utils/adminPortalHelpers";
import { getPermissionRequest } from "../../../store/Auth/actions";
import store from "../../../store";

const getPromotionStatusTagKey = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (
    normalized === promotionRequestsStatus.accepted ||
    normalized === "approved"
  ) {
    return "completed";
  }

  if (normalized === promotionRequestsStatus.rejected) {
    return "canceled";
  }

  return "pending";
};

const PromotionRequests = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const lang = useSelector((state) => state.locale.lang);
  const { promotionRequests, shared } = messages[lang];

  const isLoading = useSelector((state) => state.loader);
  const meta = useSelector((state) => state.promotionRequests.meta);
  const list = useSelector((state) => state.promotionRequests.list);
  const roles = useSelector((state) => state.lookups.general.roles);
  const { id } = store.getState().auth.login.role;
  const [searchValue, setSearchValue] = useState("");
  const [filterRoles, setFilterRoles] = useState(null);
  const [filterStatus, setFilterStatus] = useState({
    id: 3,
    name: promotionRequests.listPromotionRequests.pending,
    value: promotionRequestsStatus.notAccepted,
  });
  const [rowId, setRowId] = useState();
  const [page, setPage] = useState(1);
  const [rowsCount, setRowsCount] = useState(10);

  useEffect(() => {
    if (!isSuperAdmin() && !isAdmin() && !isSupervisorManger()) {
      dispatch(getPermissionRequest({ id }));
    }
  }, [lang]);

  const { canEdit, canDelete } = useAdminModulePermission(
    adminPermissionModules.promotions,
  );

  const statusOptions = [
    { id: 1, name: promotionRequests.listPromotionRequests.all, value: null },
    {
      id: 2,
      name: promotionRequests.listPromotionRequests.approved,
      value: promotionRequestsStatus.accepted,
    },
    {
      id: 3,
      name: promotionRequests.listPromotionRequests.pending,
      value: promotionRequestsStatus.notAccepted,
    },
    {
      id: 4,
      name: promotionRequests.listPromotionRequests.rejected,
      value: promotionRequestsStatus.rejected,
    },
  ];
  const [openActionModal, setOpenActionModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const getPromotionRequests = (pageNo, search, status, role) => {
    dispatch(
      getPromotionRequestsRequest({
        page: pageNo,
        items: rowsCount,
        user_name: search,
        status: status?.value,
        user_role_id: role?.id,
        order_by_created_at: "desc",
      })
    );
  };

  const handleConfirmModalAction = () => {
    modalType == "accept"
      ? dispatch(
          editPromotionRequestsRequest({
            data: { status: "approved" },
            id: rowId,
            page,
            items: rowsCount,
            user_name: searchValue,
            status: filterStatus?.value,
            user_role_id: filterRoles?.id,
          })
        )
      : dispatch(
          editPromotionRequestsRequest({
            data: { status: "rejected" },
            id: rowId,
            page,
            items: rowsCount,
            user_name: searchValue,
            status: filterStatus?.value,
            user_role_id: filterRoles?.id,
          })
        );

    setOpenActionModal(false);
    setRowId(null);
    setModalType(null);
  };
  useEffect(() => {
    dispatch(getLookupsRequest([lookupsKeys.roles]));
    if (filterStatus) {
      const localizedFilterValue = statusOptions.find(
        (option) => option.id === filterStatus.id
      );
      setFilterStatus(localizedFilterValue);
    }
    getPromotionRequests(page, searchValue, filterStatus, filterRoles);
  }, [lang]);

  useEffect(() => {
    getPromotionRequests(page, searchValue, filterStatus, filterRoles);
  }, [page, rowsCount]);

  useEffect(() => {
    if (
      (searchValue.length >= 3 || searchValue.length === 0) &&
      !textContainOnlySpaces(searchValue)
    ) {
      setPage(1);
      getPromotionRequests(1, searchValue, filterStatus, filterRoles);
    }
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
    getPromotionRequests(1, searchValue, filterStatus, filterRoles);
  }, [filterStatus]);

  useEffect(() => {
    setPage(1);
    getPromotionRequests(1, searchValue, filterStatus, filterRoles);
  }, [filterRoles]);

  const handleRowsCountChange = (value) => {
    setRowsCount(value);
    setPage(1);
  };

  const paginationTotal = meta?.count || 0;
  const paginationCurrentPage = meta?.currentPage || page || 1;
  const paginationFrom =
    paginationTotal === 0
      ? 0
      : (paginationCurrentPage - 1) * rowsCount + 1;
  const paginationTo = Math.min(
    paginationCurrentPage * rowsCount,
    paginationTotal
  );
  const paginationPages =
    meta?.pages || Math.ceil(paginationTotal / rowsCount) || 1;

  const renderPromotionRequestsList = () => {
    if (!isLoading && !list?.length) {
      return (
        <UIEmptyState
          title={promotionRequests.listPromotionRequests.noDataDescription}
          icon={<TrendingUp aria-hidden />}
          classNames={{ root: "py-5" }}
        />
      );
    } else {
      const rows = list.map((row) => ({
        id: row.id,
        image: row?.user?.image,
        applicantName:
          lang === "ar" ? row?.user?.full_name_ar : row?.user?.full_name_en,
        applicantNameEn: row?.user?.full_name_en,
        userId: row?.user?.id,
        deletedAt: row?.user?.deleted_at,
        role: row?.user?.role?.name,
        gender: row?.user?.gender
          ? shared.genderOptions[row?.user?.gender]
          : "-",
        rating: row?.user?.rating ? Number(row.user.rating).toFixed(2) : 0,
        requestDate: row?.created_at
          ? dayjs(row.created_at).format("DD-MM-YYYY")
          : "-",
        status: row.status,
      }));

      const columns = [
        {
          field: "image",
          headerName: shared.image,
          width: 100,
          sortable: false,
          renderCell: (params) => (
            <Avatar
              imgSrc={params.row.image}
              imgSrcAlt="user-picture"
              size={30}
              name={params.row.applicantNameEn}
            />
          ),
        },
        {
          field: "applicantName",
          headerName: promotionRequests.listPromotionRequests.applicantName,
          flex: 1.5,
          minWidth: 230,
          sortable: false,
          renderCell: (params) => (
            <span
              className={!params.row.deletedAt ? "pointer" : ""}
              onClick={() => {
                if (!params.row.deletedAt) {
                  history.push(
                    ROUTE_PATHS.viewApplicant.replace(":id", params.row.userId)
                  );
                }
              }}
            >
              {renderTextEllipsis(params.row.applicantName)}
            </span>
          ),
        },
        {
          field: "role",
          headerName: shared.role,
          flex: 0.8,
          minWidth: 120,
          sortable: false,
        },
        {
          field: "gender",
          headerName: shared.gender,
          flex: 0.8,
          minWidth: 120,
          sortable: false,
        },
        {
          field: "rating",
          headerName: promotionRequests.listPromotionRequests.profileRating,
          flex: 0.8,
          minWidth: 120,
          sortable: false,
          renderCell: (params) => <UIRating value={params.row.rating} />,
        },
        {
          field: "requestDate",
          headerName: promotionRequests.listPromotionRequests.requestDate,
          flex: 1,
          minWidth: 150,
          sortable: false,
        },
        {
          field: "status",
          headerName: shared.status,
          flex: 0.8,
          minWidth: 130,
          sortable: false,
          renderCell: (params) => (
            <UIStatusTag
              preset="table"
              status={getPromotionStatusTagKey(params.row.status)}
              label={
                promotionRequests.listPromotionRequests[params.row.status] ||
                params.row.status
              }
            />
          ),
        },
        {
          field: "actions",
          headerName: shared.actions,
          width: 90,
          minWidth: 80,
          sortable: false,
          align: "center",
          headerAlign: "center",
          renderCell: (params) => {
            const canAccept = canEdit;
            const canReject = canDelete;
            const actionsDisabled =
              params.row.status !== "pending" || !!params.row.deletedAt;

            const items = [];

            if (canAccept) {
              items.push({
                key: "accept",
                label: promotionRequests.listPromotionRequests.accept,
                disabled: actionsDisabled,
                onClick: () => {
                  setModalType("accept");
                  setRowId(params.row.id);
                  setOpenActionModal(true);
                },
              });
            }

            if (canReject) {
              items.push({
                key: "reject",
                label: promotionRequests.listPromotionRequests.reject,
                disabled: actionsDisabled,
                danger: true,
                onClick: () => {
                  setModalType("reject");
                  setRowId(params.row.id);
                  setOpenActionModal(true);
                },
              });
            }

            return (
              <UIRowActionsMenu
                ariaLabel={shared.actions}
                items={items}
              />
            );
          },
        },
      ];

      return (
        <>
          <UITable
            rows={rows}
            columns={columns}
            pageSize={rowsCount}
            contentSized
            hideFooter
            disableSelectionOnClick
          />
          {paginationTotal > 0 && (
            <UIPaginationBar
              currentPage={paginationCurrentPage}
              pages={paginationPages}
              pageSize={rowsCount}
              rangeTemplate={
                promotionRequests.listPromotionRequests.showingRange
              }
              rangeFrom={paginationFrom}
              rangeTo={paginationTo}
              rangeTotal={paginationTotal}
              rowsPerPageLabel={shared.numberOfRows}
              onPageChange={(nextPage) => setPage(nextPage)}
              onPageSizeChange={handleRowsCountChange}
              className="promotion-requests-list-container__pagination"
            />
          )}
          <UIActionModal
            open={openActionModal}
            title={
              modalType === "accept"
                ? promotionRequests.listPromotionRequests.accept
                : promotionRequests.listPromotionRequests.reject
            }
            variant={modalType === "reject" ? "reject" : "approve"}
            onClose={() => {
              setOpenActionModal(false);
              setRowId(null);
              setModalType(null);
            }}
            actions={
              <UIActionModal.Actions
                cancelLabel={shared.cancel}
                confirmLabel={shared.confirm}
                onCancel={() => {
                  setOpenActionModal(false);
                  setRowId(null);
                  setModalType(null);
                }}
                onConfirm={handleConfirmModalAction}
              />
            }
          >
            <p className="ui-action-modal__body-text promotion-requests-list-container__modal-description">
              {modalType === "accept"
                ? promotionRequests.listPromotionRequests.accpetMsg
                : promotionRequests.listPromotionRequests.rejectMsg}
            </p>
          </UIActionModal>
        </>
      );
    }
  };

  return (
    <div className="conatiner promotion-requests-list-container ">
      <div className="my-3">
        <Breadcrumbs
          list={[
            {
              path: "",
              label: promotionRequests.listPromotionRequests.list,
            },
          ]}
          BreadCrumbHasBackIcon={false}
          className="mb-4"
        />
      </div>
      <Filters
        isCollapsable
        searchInput={{
          placeholder: promotionRequests.listPromotionRequests.search,
          name: "search",
          value: searchValue,
          onChange: (value) => setSearchValue(value),
        }}
        filters={[
          {
            type: "filter",
            name: "status",
            placeholder: promotionRequests.listPromotionRequests.status,
            label: promotionRequests.listPromotionRequests.status,
            wrapperClass: "promotion-requests-list-container__filter-field",
            value: filterStatus,
            options: statusOptions,
            onChange: (value) => setFilterStatus(value),
          },
          {
            type: "filter",
            name: "role",
            placeholder: shared.role,
            label: shared.role,
            wrapperClass: "promotion-requests-list-container__filter-field",
            value: filterRoles,
            options: roles,
            onChange: (value) => setFilterRoles(value),
          },
        ]}
        classNames={{
          container: "promotion-requests-list-container__filters",
          base: "",
          search: "mb-3 mb-md-0",
          filter: "",
        }}
      />
      <div className="promotion-requests-list-container__content d-flex flex-column justify-content-between">
        {renderPromotionRequestsList()}
      </div>
    </div>
  );
};

export default PromotionRequests;
